import React, { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import LabelSelect from 'src/modules/shared/components/atoms/inputs/label-select';
import LabelSwitch from 'src/modules/shared/components/atoms/inputs/label-switch';
import LabelTextArea from 'src/modules/shared/components/atoms/inputs/label-text-area';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import SearchableSelect, {
  type SearchableSelectItem,
} from 'src/modules/shared/components/atoms/inputs/searchable-select';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { PiInfo } from 'react-icons/pi';
import {
  TCCredentialCreateBodyParams,
  TCCredentialKind,
  TCCredentialStatus,
} from 'src/api/generated';
import {
  CREDENTIAL_PROVIDER_OPTIONS,
  createDefaultCredentialCreateFormState,
  getForcedCredentialKindForProvider,
  OTHER_CREDENTIAL_PROVIDER_OPTION,
} from 'src/modules/settings/utils/credentials.util';
import {
  credentialCreateFormSchema,
  type CredentialCreateFormInputValues,
  type CredentialCreateFormValues,
} from 'src/modules/settings/validations/credentials.validation';

/**
 * Props for CredentialCreateModal component.
 */
export interface CredentialCreateModalProps {
  /** Whether the modal is open. */
  isOpen: boolean;
  /** Called when modal should be closed. */
  onClose: () => void;
  /** Called when form is submitted with a valid payload. */
  onSubmit: (body: TCCredentialCreateBodyParams) => Promise<void>;
  /** Whether the submit action is in progress. */
  loading: boolean;
}

/**
 * CredentialCreateModal component.
 */
export const CredentialCreateModal: React.FC<CredentialCreateModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
}) => {
  const { t } = useI18n();

  const defaultValues = useMemo(
    () => createDefaultCredentialCreateFormState(),
    []
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<
    CredentialCreateFormInputValues,
    unknown,
    CredentialCreateFormValues
  >({
    resolver: zodResolver(credentialCreateFormSchema),
    defaultValues,
  });

  useEffect(() => {
    if (!isOpen) return;
    reset(defaultValues);
  }, [defaultValues, isOpen, reset]);

  const providerOption = watch('providerOption');
  const kind = watch('kind');

  const forcedKind = getForcedCredentialKindForProvider(providerOption);
  const isKindLocked = !!forcedKind;

  useEffect(() => {
    if (!forcedKind) return;
    setValue('kind', forcedKind);
  }, [forcedKind, setValue]);

  const kindOptions = useMemo(
    () => [
      {
        value: TCCredentialKind.API_KEY,
        content: t('settings.credentials.kind.apiKey'),
      },
      {
        value: TCCredentialKind.BEARER,
        content: t('settings.credentials.kind.bearer'),
      },
      {
        value: TCCredentialKind.BASIC,
        content: t('settings.credentials.kind.basic'),
      },
      {
        value: TCCredentialKind.CUSTOM,
        content: t('settings.credentials.kind.custom'),
      },
    ],
    [t]
  );

  const providerOptions: SearchableSelectItem[] = useMemo(() => {
    const options: SearchableSelectItem[] = [
      { value: '', content: t('settings.credentials.provider.select') },
    ];

    if (CREDENTIAL_PROVIDER_OPTIONS.length > 0) {
      options.push({ separator: true });
      options.push(
        ...CREDENTIAL_PROVIDER_OPTIONS.map((provider) => ({
          value: provider.id,
          content: t(provider.labelKey),
        }))
      );
      options.push({ separator: true });
    }

    options.push({
      value: OTHER_CREDENTIAL_PROVIDER_OPTION,
      content: t('settings.credentials.provider.other'),
    });

    return options;
  }, [t]);

  const handleFormSubmit = async (values: CredentialCreateFormValues) => {
    const provider =
      values.providerOption === OTHER_CREDENTIAL_PROVIDER_OPTION
        ? values.otherProvider?.trim() ?? ''
        : values.providerOption;

    const name = values.name.trim();

    let secret: TCCredentialCreateBodyParams['secret'];
    switch (values.kind) {
      case TCCredentialKind.API_KEY:
        secret = { apiKey: values.apiKey?.trim() ?? '' };
        break;
      case TCCredentialKind.BEARER:
        secret = { token: values.bearerToken?.trim() ?? '' };
        break;
      case TCCredentialKind.BASIC:
        secret = {
          username: values.basicUsername?.trim() ?? '',
          password: values.basicPassword?.trim() ?? '',
        };
        break;
      case TCCredentialKind.CUSTOM:
        secret = JSON.parse(values.customSecretJson ?? '{}') as Record<
          string,
          unknown
        >;
        break;
      default:
        throw new Error('Unsupported credential kind');
    }

    await onSubmit({
      provider,
      name,
      kind: values.kind,
      status: values.status,
      sharedWithTenant: values.sharedWithTenant,
      secret,
    });
  };

  const renderSecretInputs = () => {
    switch (kind) {
      case TCCredentialKind.API_KEY:
        return (
          <LabelTextInput
            label={t('settings.credentials.form.apiKey')}
            labelPosition="top"
            inputProps={{
              ...register('apiKey'),
              error: errors.apiKey?.message,
            }}
          />
        );
      case TCCredentialKind.BEARER:
        return (
          <LabelTextInput
            label={t('settings.credentials.form.bearerToken')}
            labelPosition="top"
            inputProps={{
              ...register('bearerToken'),
              error: errors.bearerToken?.message,
            }}
          />
        );
      case TCCredentialKind.BASIC:
        return (
          <div className="space-y-3">
            <LabelTextInput
              label={t('settings.credentials.form.username')}
              labelPosition="top"
              inputProps={{
                ...register('basicUsername'),
                error: errors.basicUsername?.message,
              }}
            />
            <LabelTextInput
              label={t('settings.credentials.form.password')}
              labelPosition="top"
              inputProps={{
                ...register('basicPassword'),
                error: errors.basicPassword?.message,
              }}
            />
          </div>
        );
      case TCCredentialKind.CUSTOM:
        return (
          <LabelTextArea
            label={t('settings.credentials.form.secretJson')}
            labelPosition="top"
            inputProps={{
              ...register('customSecretJson'),
              error: errors.customSecretJson?.message,
              rows: 6,
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={
        <Text size="h6" weight="semibold">
          {t('settings.credentials.create.title')}
        </Text>
      }
      body={
        <form
          className="w-full space-y-3"
          onSubmit={handleSubmit(handleFormSubmit)}
          id="credential-create-form"
        >
          <div className="flex flex-col gap-1">
            <Text size="small" weight="medium">
              {t('settings.credentials.form.provider')}
            </Text>
            <Controller
              control={control}
              name="providerOption"
              render={({ field: { value, onChange } }) => (
                <SearchableSelect
                  size="md"
                  options={providerOptions}
                  value={value}
                  onChange={(next) => onChange(next ?? '')}
                  placeholder={t('settings.credentials.provider.select')}
                />
              )}
            />
            {errors.providerOption?.message && (
              <Text size="small" color="danger" className="mt-1">
                {errors.providerOption.message}
              </Text>
            )}
          </div>

          {providerOption === OTHER_CREDENTIAL_PROVIDER_OPTION && (
            <LabelTextInput
              label={t('settings.credentials.form.otherProvider')}
              labelPosition="top"
              inputProps={{
                ...register('otherProvider'),
                error: errors.otherProvider?.message,
                placeholder: t(
                  'settings.credentials.form.otherProviderPlaceholder'
                ),
              }}
            />
          )}

          <LabelTextInput
            label={t('settings.credentials.form.name')}
            labelPosition="top"
            inputProps={{
              ...register('name'),
              error: errors.name?.message,
            }}
          />

          <Controller
            control={control}
            name="kind"
            render={({ field: { value, onChange } }) => (
              <LabelSelect
                label={t('settings.credentials.form.kind')}
                labelPosition="top"
                options={kindOptions}
                selectProps={{
                  value,
                  disabled: isKindLocked,
                  onChange: (event: React.ChangeEvent<HTMLSelectElement>) => {
                    const nextValue = event.target.value;
                    const parsed = Object.values(TCCredentialKind).find(
                      (v) => v === nextValue
                    );
                    if (parsed) {
                      onChange(parsed);
                    }
                  },
                }}
              />
            )}
          />
          {errors.kind?.message && (
            <Text size="small" color="danger" className="mt-1">
              {errors.kind.message}
            </Text>
          )}

          <Controller
            control={control}
            name="status"
            render={({ field: { value, onChange } }) => (
              <div className="flex items-center justify-end">
                <LabelSwitch
                  label={
                    value === TCCredentialStatus.ACTIVE
                      ? t('settings.credentials.status.active')
                      : t('settings.credentials.status.disabled')
                  }
                  checked={value === TCCredentialStatus.ACTIVE}
                  onChange={(checked) =>
                    onChange(
                      checked
                        ? TCCredentialStatus.ACTIVE
                        : TCCredentialStatus.DISABLED
                    )
                  }
                  size="sm"
                />
              </div>
            )}
          />

          <Controller
            control={control}
            name="sharedWithTenant"
            render={({ field: { value, onChange } }) => (
              <div className="flex items-center justify-end">
                <LabelSwitch
                  label={t('settings.credentials.form.sharedWithUsers')}
                  checked={value ?? false}
                  onChange={onChange}
                  size="sm"
                />
              </div>
            )}
          />

          <Alert
            variant="neutral"
            appearance="soft"
            size="sm"
            icon={<PiInfo className="w-4 h-4" />}
            message={t('settings.credentials.form.sharedInfo')}
          />

          <div className="pt-1" />
          {renderSecretInputs()}

          <Alert
            variant="neutral"
            appearance="soft"
            size="sm"
            icon={<PiInfo className="w-4 h-4" />}
            message={t('settings.credentials.form.encryptionInfo')}
          />
        </form>
      }
      footer={
        <div className="flex justify-end gap-3 px-4 py-3">
          <Button
            variant="neutral"
            appearance="ghost"
            onClick={onClose}
            disabled={loading || isSubmitting}
          >
            {t('settings.credentials.actions.cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="credential-create-form"
            loading={loading || isSubmitting}
            disabled={loading || isSubmitting}
          >
            {t('settings.credentials.actions.create')}
          </Button>
        </div>
      }
      dialogClassName="max-w-lg"
    />
  );
};
